import React from "react";
import {
  sanitizeErrorMessage,
  logErrorForDebugging,
} from "../../../../utility/errorSanitization";

/**
 * Formats a number as USD currency with comprehensive error handling
 * @param {number|string|null|undefined} amount - The amount to format
 * @param {Object} options - Formatting options
 * @param {string} options.currency - Currency code (default: "USD")
 * @param {string} options.locale - Locale for formatting (default: "en-US")
 * @param {string} options.fallback - Fallback text when formatting fails (default: "$0.00")
 * @param {boolean} options.showCents - Whether to show cents (default: true)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, options = {}) => {
  const {
    currency = "USD",
    locale = "en-US",
    fallback = "$0.00",
    showCents = true,
  } = options;

  try {
    // Handle null, undefined, or invalid values
    if (amount === null || amount === undefined || isNaN(amount)) {
      return fallback;
    }

    // Convert to number if it's a string
    const numericAmount =
      typeof amount === "string" ? parseFloat(amount) : amount;

    if (isNaN(numericAmount)) {
      return fallback;
    }

    // Format the number as currency
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      minimumFractionDigits: showCents ? 2 : 0,
      maximumFractionDigits: showCents ? 2 : 0,
    }).format(numericAmount);
  } catch (err) {
    const sanitizedMessage = sanitizeErrorMessage(err, "format currency");
    logErrorForDebugging(err, "format currency", sanitizedMessage);
    return fallback;
  }
};

/**
 * React component for displaying currency values
 * @param {Object} props - Component props
 * @param {number|string|null|undefined} props.amount - The amount to display
 * @param {string} props.className - Additional CSS classes
 * @param {Object} props.formatOptions - Options passed to formatCurrency
 * @param {boolean} props.showLabel - Whether to show a label
 * @param {string} props.label - Label text
 * @param {string} props.fallback - Fallback text when amount is invalid
 * @returns {JSX.Element} Formatted currency display
 */
const CurrencyDisplay = ({
  amount,
  className = "",
  formatOptions = {},
  showLabel = false,
  label = "Amount",
  fallback = "N/A",
}) => {
  if (amount === null || amount === undefined || amount === "") {
    return <span className={`text-gray-500 ${className}`}>{fallback}</span>;
  }

  const formattedAmount = formatCurrency(amount, formatOptions);

  return (
    <span className={`text-gray-700 ${className}`}>
      {showLabel && <span className="font-medium">{label}: </span>}
      {formattedAmount}
    </span>
  );
};

export default CurrencyDisplay;
